import getCookie from '@capsule/get-cookie'
import CapsulePmcBridge from '@capsule/pmc-bridge'

/**
 * PMCBridge to centralize all exchanges with the PMC API
 */
export default class PmcBridge extends CapsulePmcBridge {
	/**
	 * Get the PMC profile id from API
	 * @async
	 * @returns Return the PMC profile id with a Promise
	 */
	async getProfileId(): Promise<string> {
		return new Promise((resolve, reject) => {
			window.pmcstarter(async (myPmc) => {
				try {
					resolve(await myPmc.getProfileId())
				} catch (error) {
					console.warn('PMCBridge::getProfileId error', error)
					reject(error)
				}
			})
		})
	}

	/**
	 * Check if PMC is connected
	 * @async
	 * @returns Return the status of the PMC connection with a Promise
	 */
	async isConnected(): Promise<boolean | Error> {
		return new Promise((resolve, reject) => {
			window.pmcstarter(async (myPmc) => {
				try {
					const isConnected = await myPmc.isConnected()
					resolve(isConnected)
				} catch (error) {
					console.warn('PMCBridge::isConnected error', error)
					reject(error)
				}
			})
		})
	}

	/**
	 * Get the current environment
	 * @async
	 * @returns Current env
	 */
	async getConfigEnv(): Promise<string> {
		return new Promise((resolve) => {
			window.pmcstarter((myPmc) => {
				resolve(myPmc.config.env)
			})
		})
	}

	/**
	 * Is the user premium
	 * Cookie name is prefixed by environment (except prod)
	 * @async
	 * @returns User has PassPrisma
	 */
	async hasPassPrisma(): Promise<boolean> {
		const env = await this.getConfigEnv()
		const prefix = env === 'prod' ? '' : `${env}-`
		return !!getCookie(`${prefix}pmc-premium`)
	}

	/**
	 * Returns the paid status of a user
	 * @returns {array} The paid status of the user
	 */
	async getAllSubscriptions(): Promise<string[]> {
		return new Promise((resolve) => {
			window.pmcstarter(async (myPmc) => {
				const pmcProfile = await myPmc.getProfile()

				if (pmcProfile) {
					const rights = pmcProfile?.paymentSubscriptions?.rights
					resolve(rights)
				} else {
					resolve([])
				}
			})
		})
	}

	/**
	 * Returns the paid status of a user
	 * @returns {boolean} The paid status of the user
	 */
	async hasSubscription(): Promise<boolean> {
		return new Promise((resolve) => {
			window.pmcstarter(async (myPmc) => {
				const pmcProfile = await myPmc.getProfile()

				if (pmcProfile) {
					const subscription = pmcProfile?.paymentSubscriptions?.rights.filter((key) => {
						return key === 'paid-web-content' || key === 'paid-web-magazine'
					})
					if (subscription?.length) {
						resolve(true)
					} else {
						resolve(false)
					}
				} else {
					resolve(false)
				}
			})
		})
	}

	/**
	 * Get the PMC profile avatar from API
	 * @async
	 * @returns Return the PMC profile id with a Promise
	 */
	async getAvatar(): Promise<string | false> {
		return new Promise((resolve, reject) => {
			window.pmcstarter(async (myPmc) => {
				try {
					const defaultAvatar = '##DEFAULT_AVATAR##'
					const avatar = await myPmc.getAvatar(defaultAvatar)
					if (avatar === defaultAvatar) {
						resolve(false)
					} else {
						resolve(avatar)
					}
				} catch (error) {
					console.warn('PMCBridge::getAvatar error', error)
					reject(error)
				}
			})
		})
	}
}
